/**
 * Inventory Adjustments - Selection Dialog
 * Shows 3 cards: Add Stock, Transfer, Usage
 * Each opens its own dedicated stepper
 * UI matches ProductInventoryAdjustment.jsx exactly
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Card, Grid,
  IconButton, useTheme, alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  SwapHoriz as SwapHorizIcon,
  Tune as TuneIcon
} from '@mui/icons-material';

export default function InventoryAdjustments({
  open,
  onClose,
  onSelectAdd,
  onSelectTransfer,
  onSelectUsage,
  inventoryType
}) {
  const theme = useTheme();
  
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          margin: 'auto',
          borderRadius: 3,
          border: 'none',
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)',
          boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <TuneIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5 }}>
              Manage Inventory
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Choose the type of inventory adjustment you want to make
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.04)',
              transform: 'rotate(90deg)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>
      
      <DialogContent sx={{
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: 360
      }}>
        <Box>
          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', textAlign: 'center', fontSize: '0.95rem' }}>
            Choose the type of inventory adjustment you want to make
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card
                onClick={onSelectAdd}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                  bgcolor: 'background.paper',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.2)}`,
                    border: `2px solid ${theme.palette.success.main}`,
                    bgcolor: alpha(theme.palette.success.main, 0.02)
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2.5
                  }}>
                    <AddIcon sx={{ fontSize: 36, color: 'success.main' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}>
                    Add Stock
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    Increase inventory levels by adding new stock
                  </Typography>
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card
                onClick={onSelectUsage}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                  bgcolor: 'background.paper',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.error.main, 0.2)}`,
                    border: `2px solid ${theme.palette.error.main}`,
                    bgcolor: alpha(theme.palette.error.main, 0.02)
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2.5
                  }}>
                    <RemoveIcon sx={{ fontSize: 36, color: 'error.main' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}>
                    Usage
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    Remove units from inventory due to usage or consumption
                  </Typography>
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card
                onClick={onSelectTransfer}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                  bgcolor: 'background.paper',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${alpha('#1976d2', 0.2)}`,
                    border: `2px solid #1976d2`,
                    bgcolor: alpha('#1976d2', 0.02)
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: alpha('#1976d2', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2.5
                  }}>
                    <SwapHorizIcon sx={{ fontSize: 36, color: '#1976d2' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}>
                    Transfer
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    Move inventory between different locations
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
